/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Partners Service - Super Admin Partners API
 * ============================================================================
 *
 * Service layer for partner management API calls.
 *
 * ============================================================================
 */

import { get, post, put } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, PaginationParams, SpringPageResponse, transformPageResponse } from '@/services/api/types';
import {
  Partner,
  PartnerListItem,
  PartnerDetail,
  PartnerReferral,
  CreatePartnerRequest,
  PartnerFilterParams,
  PartnerStatus,
  PartnerType,
} from '@/types/superadmin.types';

const BASE_URL = '/superadmin/partners';

/**
 * Backend partner response (raw entity fields)
 */
interface BackendPartner {
  id: string;
  code: string;
  companyName: string;
  contactName: string;
  email: string;
  phone?: string;
  address?: string;
  partnerType: string;
  status: string;
  commissionRate: number;
  commissionDurationMonths?: number;
  autoApproveCommissions: boolean;
  totalReferrals: number;
  activeReferrals: number;
  totalEarnings: number;
  pendingBalance: number;
  withdrawnTotal?: number;
  paymentMethod?: string;
  paymentDetails?: string;
  createdAt: string;
}

/**
 * Map backend status to frontend status (direct mapping)
 * Backend: ACTIVE, SUSPENDED, PENDING, TERMINATED
 */
function mapPartnerStatus(backendStatus: string): PartnerStatus {
  switch (backendStatus) {
    case 'ACTIVE': return 'ACTIVE';
    case 'PENDING': return 'PENDING';
    case 'SUSPENDED': return 'SUSPENDED';
    case 'TERMINATED': return 'TERMINATED';
    default: return 'ACTIVE';
  }
}

/**
 * Transform backend partner to frontend list item format
 */
function transformPartnerListItem(backend: BackendPartner): PartnerListItem {
  return {
    id: backend.id,
    name: backend.contactName || backend.companyName,
    email: backend.email,
    company: backend.companyName,
    type: backend.partnerType as PartnerType,
    status: mapPartnerStatus(backend.status),
    commissionRate: Number(backend.commissionRate),
    autoApproveCommissions: backend.autoApproveCommissions ?? false,
    activeReferrals: backend.activeReferrals || 0,
    totalEarnings: Number(backend.totalEarnings) || 0,
    pendingPayout: Number(backend.pendingBalance) || 0,
    joinedAt: backend.createdAt,
  };
}

/**
 * Transform backend partner to frontend full partner format
 */
function transformPartner(backend: BackendPartner): Partner {
  return {
    id: backend.id,
    name: backend.contactName || backend.companyName,
    email: backend.email,
    phone: backend.phone || '',
    company: backend.companyName,
    type: backend.partnerType as PartnerType,
    status: mapPartnerStatus(backend.status),
    commissionRate: Number(backend.commissionRate),
    autoApproveCommissions: backend.autoApproveCommissions ?? false,
    inviteCode: backend.code,
    totalReferrals: backend.totalReferrals || 0,
    activeReferrals: backend.activeReferrals || 0,
    totalEarnings: Number(backend.totalEarnings) || 0,
    pendingPayout: Number(backend.pendingBalance) || 0,
    address: backend.address,
    bankDetails: backend.paymentDetails,
    joinedAt: backend.createdAt,
    createdAt: backend.createdAt,
    updatedAt: backend.createdAt, // Use createdAt as fallback
  };
}

/**
 * Generate a unique partner code from name
 */
function generatePartnerCode(name: string): string {
  // Remove special characters, convert to uppercase, take first 10 chars
  const base = name
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .substring(0, 10);
  // Add random suffix for uniqueness
  const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${base}_${suffix}`;
}

/**
 * Map frontend status to backend status (direct mapping now)
 * Both use: ACTIVE, SUSPENDED, PENDING, TERMINATED
 */
function mapStatusToBackend(status: string): string {
  return status;
}

/**
 * Transform frontend create request to backend format
 */
function transformCreateRequest(data: CreatePartnerRequest): Record<string, unknown> {
  return {
    code: generatePartnerCode(data.company || data.name),
    contactName: data.name,
    companyName: data.company || data.name,
    email: data.email,
    phone: data.phone,
    address: data.address,
    partnerType: data.type,
    commissionRate: data.commissionRate,
    autoApproveCommissions: data.autoApproveCommissions ?? false,
    paymentDetails: data.bankDetails,
  };
}

/**
 * Partners service for Super Admin
 */
export const partnersService = {
  /**
   * Get paginated list of partners
   * Backend uses path parameter for status: /status/{status}
   */
  async getPartners(
    params?: PaginationParams & PartnerFilterParams
  ): Promise<PaginatedResponse<PartnerListItem>> {
    // Extract status and build correct URL
    const { status, ...otherParams } = params || {};

    // Use /status/{status} endpoint if status is provided, otherwise get all
    const url = status ? `${BASE_URL}/status/${status}` : BASE_URL;

    const response = await get<ApiResponse<SpringPageResponse<BackendPartner>>>(url, otherParams);
    const transformed = transformPageResponse(response.data);
    return {
      ...transformed,
      content: transformed.content.map(transformPartnerListItem),
    };
  },

  /**
   * Get partner by ID
   */
  async getPartner(id: string): Promise<PartnerDetail> {
    const response = await get<ApiResponse<BackendPartner>>(`${BASE_URL}/${id}`);
    const partner = transformPartner(response.data);
    // Return as PartnerDetail with empty referrals (would need separate API call)
    return { ...partner, referrals: [] };
  },

  /**
   * Create a new partner
   */
  async createPartner(data: CreatePartnerRequest): Promise<Partner> {
    const backendData = transformCreateRequest(data);
    const response = await post<ApiResponse<BackendPartner>>(BASE_URL, backendData);
    return transformPartner(response.data);
  },

  /**
   * Update a partner
   */
  async updatePartner(id: string, data: Partial<CreatePartnerRequest> & { status?: string }): Promise<Partner> {
    // Transform to backend format
    const backendData: Record<string, unknown> = {};
    if (data.name) backendData.contactName = data.name;
    if (data.company) backendData.companyName = data.company;
    if (data.email) backendData.email = data.email;
    if (data.phone !== undefined) backendData.phone = data.phone;
    if (data.address !== undefined) backendData.address = data.address;
    if (data.type) backendData.partnerType = data.type;
    if (data.commissionRate !== undefined) backendData.commissionRate = data.commissionRate;
    if (data.autoApproveCommissions !== undefined) backendData.autoApproveCommissions = data.autoApproveCommissions;
    if (data.bankDetails !== undefined) backendData.paymentDetails = data.bankDetails;
    if (data.status) backendData.status = mapStatusToBackend(data.status);

    const response = await put<ApiResponse<BackendPartner>>(`${BASE_URL}/${id}`, backendData);
    return transformPartner(response.data);
  },

  /**
   * Get partner's referrals
   */
  async getPartnerReferrals(id: string): Promise<PartnerReferral[]> {
    const response = await get<ApiResponse<PartnerReferral[]>>(`${BASE_URL}/${id}/referrals`);
    return response.data;
  },

  /**
   * Activate a partner
   */
  async activatePartner(id: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${id}/activate`);
  },

  /**
   * Suspend a partner
   */
  async suspendPartner(id: string, reason?: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${id}/suspend`, { reason });
  },

  /**
   * Terminate a partner
   */
  async terminatePartner(id: string, reason?: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${id}/terminate`, { reason });
  },

  /**
   * Get partner statistics
   */
  async getPartnerStats(): Promise<{ activePartners: number; totalPendingBalance: number }> {
    const response = await get<ApiResponse<{ activePartners: number; totalPendingBalance: number }>>(`${BASE_URL}/stats`);
    return response.data;
  },
};

export default partnersService;
